<script lang="ts" setup>
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import AdminBaseTable, { Field } from '@/components/admin/admin-base-table.vue';
import Avatar from '@/components/common/atoms/avatar.vue';
import Loading from '@/components/common/loading.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import {
  CREDIT_BALANCE_ADJUSTED_MESSAGE,
  USAGE_TYPE_DISPLAY_NAME,
} from '@/utils/credit/constants';
import { formatDate } from '@/utils/formatters';
import { STATES, useManagementContractCreditUsages } from '@/utils/swr';

type AdminTableItem = {
  id: number;
  userId: number;
  userName: string;
  userImageUrl: string;
  userEmail: string;
  usageType: string;
  userIsDeleted: boolean;
  creditCost: number;
  createdAt: string;
  formattedCreatedAt: string;
  isCreditCostAdjusted: boolean;
};

const props = defineProps<{
  selectedCreditPeriodId?: number;
}>();

const route = useRoute();
const { createSnackbar } = useSnackbar();
const contractId = computed(() => Number(route.params.contractId));

const creditCreditPeriodId = computed(() => props.selectedCreditPeriodId ?? 0);
const { data: creditUsagesData, state: creditUsagesState } =
  useManagementContractCreditUsages(contractId, creditCreditPeriodId);
const isLoadingCreditUsages = computed(
  () =>
    creditUsagesState.value === STATES.PENDING ||
    creditUsagesState.value === STATES.VALIDATING,
);
const creditUsages = computed(() => creditUsagesData.value ?? []);

const items = computed<AdminTableItem[]>(() => {
  return creditUsages.value.map(usage => {
    const user = usage.user;
    return {
      id: usage.id,
      userId: user?.id ?? 0,
      userName: user?.user_name ?? '',
      userImageUrl: user?.image_url ?? '',
      userEmail: user?.email ?? '',
      usageType: USAGE_TYPE_DISPLAY_NAME[usage.usage_type],
      userIsDeleted: user?.is_deleted ?? true,
      creditCost: usage.committed_usage_credit ?? usage.requested_usage_credit,
      createdAt: usage.created_at,
      formattedCreatedAt: formatDate(usage.created_at),
      // committed_usage_creditがnull でなく かつ requested_usage_creditと異なる場合はクレジットが調整されているとみなす
      isCreditCostAdjusted:
        usage.committed_usage_credit !== null &&
        usage.committed_usage_credit !== usage.requested_usage_credit,
    };
  });
});

watch(creditUsagesState, () => {
  if (creditUsagesState.value === STATES.ERROR) {
    createSnackbar({
      message: '利用履歴の取得に失敗しました。',
      type: 'error',
    });
  }
});

const fields: Field<AdminTableItem>[] = [
  {
    name: 'userName',
    displayName: '利用者',
    isSortable: true,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'usageType',
    displayName: '利用エージェント',
    isSortable: true,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'creditCost',
    displayName: '利用量',
    isSortable: true,
    type: 'number',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'createdAt',
    displayName: '利用日',
    isSortable: true,
    type: 'date',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
];
const initialSortField = fields.find(f => f.name === 'createdAt');
</script>

<template>
  <Loading class="loading" v-if="isLoadingCreditUsages" />
  <div v-else>
    <div class="title">
      <span class="c-title-bold--m">利用履歴</span
      ><span class="count c-text-regular--s">・{{ items.length }}件</span>
    </div>
    <div class="spacing-16"></div>
    <AdminBaseTable
      :items="items"
      :fields="fields"
      :initial-sort-field="initialSortField"
      initial-sort-order="desc"
    >
      <template #empty-tbody>利用履歴はありません。</template>
      <template #cell-userName="{ item }">
        <div class="user-cell">
          <template v-if="!item.userIsDeleted">
            <router-link :to="`/users/${item.userId}`">
              <Avatar :image-url="item.userImageUrl" size="xs"></Avatar>
            </router-link>
          </template>
          <template v-else>
            <Avatar :image-url="item.userImageUrl" size="xs"></Avatar>
          </template>
          <div class="name-and-email">
            <template v-if="!item.userIsDeleted">
              <router-link :to="`/users/${item.userId}`">
                <div class="name c-text-regular--m">
                  {{ item.userName }}
                </div>
              </router-link>
            </template>
            <template v-else>
              <div class="name no-underline c-text-regular--m">
                {{ item.userName }}
              </div>
            </template>
            <div class="email c-text-regular--xs">{{ item.userEmail }}</div>
          </div>
        </div>
      </template>
      <template #cell-creditCost="{ item }">
        <div class="credit-cost-cell">
          {{ item.creditCost.toLocaleString() }}
          <DgrIcon
            v-if="item.isCreditCostAdjusted"
            v-tooltip.bottom="{
              content: CREDIT_BALANCE_ADJUSTED_MESSAGE,
            }"
            size="small"
            name="exclamation-circle"
          />
        </div>
      </template>
      <template #cell-createdAt="{ item }">
        {{ item.formattedCreatedAt }}
      </template>
    </AdminBaseTable>
  </div>
</template>

<style lang="scss" scoped>
.loading {
  margin-top: -24px; // 先祖のsrc/components/layouts/admin-content.vueのmargin-topを相殺
  height: calc(
    100vh - 64px - 91px - 41px
  ); // 画面全体の高さ - グローバルヘッダーの高さ - ページヘッダーの高さ - タブの高さ
}
.title {
  display: flex;
  align-items: center;
  .count {
    color: $color-gray600;
  }
}
.user-cell {
  display: flex;
  align-items: center;
  min-width: 180px;
  .name-and-email {
    width: calc(100% - 16px);
    margin-left: 12px;
    .name {
      word-wrap: break-word;
      overflow-wrap: break-word;
      text-decoration: underline;
      &.no-underline {
        text-decoration: none;
      }
    }
    .email {
      color: $color-gray600;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
  }
}
.credit-cost-cell {
  display: flex;
  gap: 4px;
  align-items: center;
}
</style>
